import {
    Alert,
    Autocomplete,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Typography
} from "@mui/material";
import UserTextBox from "./UserTextBox";
import {pwcEmailValidatorSync, UserInfo, UserSearchInfo} from "../services/common";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import React, {SetStateAction, useRef} from "react";
import {ApplicationItem, optionsTrueFalseEmpty, StatusHistory} from "../services/applicationsApi";
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import ErrorIcon from '@mui/icons-material/Error';
import CloseIcon from '@mui/icons-material/Close';
import './AppDetails.css';
import ListedContactTextBox from "./ListedContactTextBox";
import AppOwnerSelect from "./AppOwnerSelect";
import utc from 'dayjs/plugin/utc';
import AppDetailsEntraInfo from "./AppDetailsEntraInfo";
import AppDetailsTemporaryPrerequisites, {isBlocked} from "./AppDetailsTemporaryPrerequisites";
import HtmlTooltip from "./HtmlTooltip";
import StatusTimeline from "./StatusTimeline";

interface AppDetailsProps {
    isModalOpen: boolean;
    model: ApplicationItem | null;
    handleClose: () => Promise<void>;
    isUrnValid: boolean;
    isViewMode: boolean;
    setIsViewMode: React.Dispatch<SetStateAction<boolean>>;
    userInfo: UserInfo | null;
    handleSave: () => Promise<void>;
    handleElementUpdate: (updatedApp: ApplicationItem, closeModals: boolean) => Promise<void>;
    statusTransitions: Map<string, string[]> | null;
}

const AppDetails = ({ isModalOpen, model, handleClose, isUrnValid, isViewMode, setIsViewMode, userInfo, handleSave, handleElementUpdate, statusTransitions }: AppDetailsProps) => {
    const [modelOrgStatus, setModelOrgStatus] = React.useState<string | null>(model?.status);
    const [isEmailValid, setIsEmailValid] = React.useState<boolean>(true);
    const [isIntEngValid, setIsIntEngValid] = React.useState<boolean>(true);
    const [isFailedReasonVisible, setIsFailedReasonVisible] = React.useState<boolean>(false);
    const [isSaveButtonEnabled, setIsSaveButtonEnabled] = React.useState<boolean>(false);
    const [isFailedReasonDisabled, setIsFailedReasonDisabled] = React.useState<boolean>(true);
    const [modelFailedReason, setModelFailedReason] = React.useState<string | null>(model?.failedReason);
    const [modelStatus, setModelStatus] = React.useState<string | null>(model?.status);
    const [modelUserEmail, setModelUserEmail] = React.useState<string | null>(model?.owner);
    const [modelUserName, setModelUserName] = React.useState<string | null>(model?.ownerName);
    const [modelIdasTeamEmail, setModelIdasTeamEmail] = React.useState<string | null>(model?.idasTeamEmail);
    const [modelIdasTeamName, setModelIdasTeamName] = React.useState<string | null>(model?.idasTeamName);
    const [modelExtAuthLevel, setModelExtAuthLevel] = React.useState<string | null>(model?.extAuthLevel);
    const [modelIsGum, setModelIsGum] = React.useState<boolean | null>(model?.isGum);
    const [modelIsLdap, setModelIsLdap] = React.useState<boolean | null>(model?.isLdap);
    const [modelListedContact, setModelListedContact] = React.useState<UserSearchInfo[]>([]);
    const [isAppOwnerSelectModalVisible, setIsAppOwnerSelectModalVisible] = React.useState<boolean>(false);
    const [modelOwnerConsentDate, setModelOwnerConsentDate] = React.useState<Date | null>(model?.ownerConsentDate);
    const [modelAppDelegate1, setModelAppDelegate1] = React.useState<string | null>(model?.appDelegate1);
    const [modelAppDelegate1Name, setModelAppDelegate1Name] = React.useState<string | null>(model?.appDelegate1Name);
    const [modelAppDelegate2, setModelAppDelegate2] = React.useState<string | null>(model?.appDelegate1);
    const [modelAppDelegate2Name, setModelAppDelegate2Name] = React.useState<string | null>(model?.appDelegate2Name);
    const [modelPrimaryApplicationOwner, setModelPrimaryApplicationOwner] = React.useState<string | null>(model?.primaryOwnerEmail);
    const [modelSecondaryApplicationOwner, setModelSecondaryApplicationOwner] = React.useState<string | null>(model?.secondaryOwnerEmail);
    const [modelItApplicationApplicationOwner, setModelItApplicationApplicationOwner] = React.useState<string | null>(model?.itApplicationOwnerEmail);
    const [modelStatusHistory, setModelStatusHistory] = React.useState<StatusHistory[] | null>(model?.statusHistory);
    const timelineWrapperRef = useRef<HTMLDivElement>(null);
    const slotProps = { inputLabel: { shrink: true } };
    const decommisionButtonVisible = (): boolean => {
        if (model == null) {
            return false;
        }
        if (userInfo?.isAdminOrMt !== true && userInfo?.isAppOwner !== true) {
            return false;
        }
        if (model.status == null) {
            return true;
        }
        if (model.status === 'To Be Decommissioned' || model.status === 'Decommissioned' || statusTransitions == null || statusTransitions[model.status] == null || statusTransitions[model.status].length < 2) {
            return false;
        }
        return statusTransitions[model.status].includes('To Be Decommissioned');
    };
    const handleDecommision = async () => {
        setModelStatus('To Be Decommissioned');
        model.status = 'To Be Decommissioned';
        await handleSave();
    };
    React.useEffect(() => {
        dayjs.extend(utc);
    }, []);
    React.useEffect(() => {
        if (modelStatus === "Failed")
        {
            setIsFailedReasonDisabled(isViewMode || modelOrgStatus === "Failed");
            setIsFailedReasonVisible(true);
            setIsSaveButtonEnabled(modelFailedReason != null && modelFailedReason?.length > 0 && isEmailValid && isIntEngValid);
        }
        else
        {
            setIsFailedReasonDisabled(true);
            setIsFailedReasonVisible(false);
            setIsSaveButtonEnabled(isEmailValid && isIntEngValid);
        }
    }, [isEmailValid, isFailedReasonDisabled, modelStatus, modelFailedReason, isViewMode, modelOrgStatus, isIntEngValid]);
    React.useEffect(() => {
        if (model != null) {
            model.status = modelStatus;
            model.extAuthLevel = modelExtAuthLevel;
            model.isGum = modelIsGum;
            model.isLdap = modelIsLdap;
            model.owner = modelUserEmail;
            model.ownerName = modelUserName;
            model.idasTeamEmail = modelIdasTeamEmail;
            model.idasTeamName = modelIdasTeamName;
            model.listedContact = getListedContactsAsString(modelListedContact);
            model.failedReason = modelFailedReason;
            model.ownerConsentDate = modelOwnerConsentDate;
            model.appDelegate1 = modelAppDelegate1;
            model.appDelegate1Name = modelAppDelegate1Name;
            model.appDelegate2 = modelAppDelegate2;
            model.appDelegate2Name = modelAppDelegate2Name;
            model.primaryOwnerEmail = modelPrimaryApplicationOwner;
            model.secondaryOwnerEmail = modelSecondaryApplicationOwner;
            model.itApplicationOwnerEmail = modelItApplicationApplicationOwner;
            model.statusHistory = modelStatusHistory;
        }
    }, [modelStatus, modelExtAuthLevel, modelIsGum, modelIsLdap, modelUserEmail, modelUserName, modelListedContact, modelFailedReason, modelOwnerConsentDate, modelIdasTeamEmail, modelIdasTeamName, modelAppDelegate1, modelAppDelegate2, modelAppDelegate1Name, modelAppDelegate2Name, modelPrimaryApplicationOwner, modelSecondaryApplicationOwner, modelItApplicationApplicationOwner, modelStatusHistory]);
    React.useEffect(() => {
        setModelOrgStatus(model?.status);
        setModelStatus(model?.status);
        setModelExtAuthLevel(model?.extAuthLevel);
        setModelIsGum(model?.isGum);
        setModelIsLdap(model?.isLdap);
        setModelUserEmail(model?.owner);
        setModelUserName(model?.ownerName);
        setModelIdasTeamEmail(model?.idasTeamEmail);
        setModelIdasTeamName(model?.idasTeamName);
        setListedContacts(model?.listedContact);
        setModelFailedReason(model?.failedReason);
        setModelOwnerConsentDate(model?.ownerConsentDate);
        setModelAppDelegate1(model?.appDelegate1);
        setModelAppDelegate2(model?.appDelegate2);
        setModelAppDelegate1Name(model?.appDelegate1Name);
        setModelAppDelegate2Name(model?.appDelegate2Name);
        setModelPrimaryApplicationOwner(model?.primaryOwnerEmail);
        setModelSecondaryApplicationOwner(model?.secondaryOwnerEmail);
        setModelItApplicationApplicationOwner(model?.itApplicationOwnerEmail);
        setModelStatusHistory(model?.statusHistory);
    }, [model, isModalOpen]);

    const setListedContacts = (x: string | null): void =>
    {
        if (x == null || x?.length === 0)
        {
            setModelListedContact([]);
            return;
        }
        const result = new Array<UserSearchInfo>();
        const emailList = x.split(",");
        for (let i = 0; i < emailList.length; i++)
        {
            if (pwcEmailValidatorSync(emailList[i]) === null)
            {
                result.push({ email: emailList[i] });
            }
        }
        setModelListedContact(result);
    };
    const getListedContactsAsString = (x: UserSearchInfo[]): string => {
        if (x == null || x.length == 0) {
            return '';
        }
        const result = Array<string>();
        for (let i = 0; i < x.length; i++)
        {
            if (pwcEmailValidatorSync(x[i].email) === null)
            {
                result.push(x[i].email);
            }
        }
        return result.toString();
    };
    const onSaveClick = async () => {
        if (userInfo?.isAdminOrMt !== true && model?.disabledForOwner !== false)
        {
            await handleSave();
        }
        else
        {
            if (modelUserEmail == null || modelUserEmail?.length == 0)
            {
                setIsAppOwnerSelectModalVisible(true);
            }
            else
            {
                await handleSave();
            }
        }
    };
    const handleOwnerSelectClose = async () =>
    {
        setIsAppOwnerSelectModalVisible(false);
    };
    return (
        <Dialog
            className="details-apps-dialog"
            open={isModalOpen}
            onClose={handleClose}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
        >
            <DialogTitle className="details-apps-header" id="scroll-dialog-title" sx={{ minWidth: '840px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: '48px' }}>
                <div>
                    {isViewMode ? "View: " : "Edit: "} {model?.name}<br/>
                    <p className="details-apps-title">{model?.urn}</p>
                </div>
                <HtmlTooltip title="Please note that data correction is an ongoing process; therefore, it is not necessary to wait until all data has been corrected before proceeding with application migration.">
                <Button
                    style={{ margin: '10px', float: 'left', width: '220px', backgroundColor: 'orange', textTransform: 'none' }}
                    variant="contained" color="success"
                    size="small"
                    sx={{ marginRight: '8px' }}
                    onClick={() => window.open('https://migration.idaas.pwc.com/docs/migration-activities/portal-migration-guide#updating-application-data', '_blank')}
                >
                    Data Update Guide
                </Button>
                </HtmlTooltip>
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={(theme) => ({
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: theme.palette.grey[500],
                })}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent dividers={true} sx={{ minWidth: '840px' }}>
                    {isUrnValid ? '' : <Alert severity="warning">Please keep in mind that URN in a UUID format is not allowed in EntraID and will be changed during the migration process.<br />URN will be changed to urn_{model?.urn}.</Alert>}
                    <Typography className="details-apps-group">Current data</Typography>
                    <Typography className="details-apps-title">The metadata associated with your application in PwC Identity</Typography>
                <div>
                    <HtmlTooltip title="This is the application name as it appears in the Service Now CMDB">
                        <TextField slotProps={slotProps} className="details-single-apps-text" margin="dense" id="editItem_name" label="Application name" defaultValue={model?.name} disabled={true} />
                    </HtmlTooltip>
                    </div>
                    <div>
                        <HtmlTooltip
                            title="This is the Technical Owner name as it appears in the Service Now CMDB"
                            hideTooltipOnChildrenExpand={true}
                        >
                            <UserTextBox canBeEmpty={true} handleValidChange={setIsEmailValid} handleChange={(e) => { setModelUserEmail(e?.email); setModelUserName(e?.name); }} label="Technical Owner" defaultValue={{ email: modelUserEmail, name: modelUserName }} disabled={isViewMode || (userInfo?.isAdminOrMt !== true && model?.disabledForOwner !== false)} />
                        </HtmlTooltip>

                        <HtmlTooltip
                            title="This is the Primary Owner name as it appears in the Service Now CMDB"
                            hideTooltipOnChildrenExpand={true}
                        >
                            <UserTextBox canBeEmpty={true} handleValidChange={setIsEmailValid} handleChange={(e) => { setModelPrimaryApplicationOwner(e?.email); }} label="Primary Application Owner" defaultValue={{ email: modelPrimaryApplicationOwner }} disabled={true} />
                        </HtmlTooltip>
                    </div>
                <div>
                    <HtmlTooltip
                        title="This is the Secondary Owner name as it appears in the Service Now CMDB"
                        hideTooltipOnChildrenExpand={true}
                    >
                        <UserTextBox canBeEmpty={true} handleValidChange={setIsEmailValid} handleChange={(e) => { setModelSecondaryApplicationOwner(e?.email); }} label="Secondary Application Owner" defaultValue={{ email: modelSecondaryApplicationOwner }} disabled={true} />
                    </HtmlTooltip>

                    <HtmlTooltip
                        title="This is the IT Applicaton Owner name as it appears in the Service Now CMDB"
                        hideTooltipOnChildrenExpand={true}
                    >
                        <UserTextBox canBeEmpty={true} handleValidChange={setIsEmailValid} handleChange={(e) => { setModelItApplicationApplicationOwner(e?.email); }} label="Application IT Owner" defaultValue={{ email: modelItApplicationApplicationOwner }} disabled={true} />
                    </HtmlTooltip>
                    </div>
                <div>
                    <HtmlTooltip
                        title="Editable within the portal, a delegate has the same rights as an application owner and can initiate migrations and edit allowed fields within the portal"
                        hideTooltipOnChildrenExpand={true}
                    >
                        <UserTextBox canBeEmpty={true} handleValidChange={setIsEmailValid} handleChange={(e) => { setModelAppDelegate1(e?.email); setModelAppDelegate1Name(e?.name) }} label="App delegate 1" defaultValue={{ email: modelAppDelegate1, name: modelAppDelegate1Name }} disabled={isViewMode || (userInfo?.isAdminOrMt !== true && model?.disabledForOwner !== false)} />
                    </HtmlTooltip>

                    <HtmlTooltip
                        title="Editable within the portal, a delegate has the same rights as an application owner and can initiate migrations and edit allowed fields within the portal"
                        hideTooltipOnChildrenExpand={true}
                    >
                        <UserTextBox canBeEmpty={true} handleValidChange={setIsEmailValid} handleChange={(e) => { setModelAppDelegate2(e?.email); setModelAppDelegate2Name(e?.name) }} label="App delegate 2" defaultValue={{ email: modelAppDelegate2, name: modelAppDelegate2Name }} disabled={isViewMode || (userInfo?.isAdminOrMt !== true && model?.disabledForOwner !== false)} />
                    </HtmlTooltip>
                    </div>
                <div>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <HtmlTooltip
                            title="App owners and delegates should fill out this field when they have planned a date to migrate this application"
                        >
                            <div style={{display: 'inline-block'}}>
                                <DatePicker
                                    timezone="UTC"
                                    sx={{ margin: '10px 5px', width: '370px' }}
                                    format="YYYY-MM-DD"
                                    onChange={(e) => model.migrationDate = dayjs(e).toDate()}
                                    label="Migration date"
                                    defaultValue={(model?.migrationDate == null) ? null : dayjs(model?.migrationDate)}
                                    disabled={isViewMode || (userInfo?.isAdminOrMt !== true && model?.disabledForOwner !== false)}
                                />
                            </div>
                        </HtmlTooltip>
                    </LocalizationProvider>

                    <HtmlTooltip
                        title="Current status of application in real time, please refer to the docusite for meaning"
                        hideTooltipOnChildrenExpand={true}
                    >
                        {isViewMode
                                ? <TextField
                                    slotProps={slotProps}
                                    className="details-apps-text"
                                    margin="dense"
                                    id="editItem_status"
                                    label="Migration status"
                                    defaultValue={modelOrgStatus}
                                    disabled={true} />
                                : <Autocomplete
                                    disableClearable
                                    disabled={userInfo?.isAdminOrMt !== true && model?.ownerCanEditStatus !== true}
                                    className="details-apps-autocomplete"
                                    value={modelStatus}
                                    onChange={(e, v) => setModelStatus(v)}
                                    options={statusTransitions == null || modelOrgStatus == null || statusTransitions[modelOrgStatus] == null ? ['Not Ready'] : statusTransitions[modelOrgStatus]}
                                    renderInput={(params) =>
                                        <TextField {...params}
                                           slotProps={slotProps}
                                           className="details-apps-autocomplete-inner"
                                           margin="dense"
                                           label="Status"
                                           onKeyDown={(e) => e.preventDefault()} />
                                    }
                                />
        }
                    </HtmlTooltip>

                    </div>
                <div>
                    <HtmlTooltip title="URN of the application in question - data derived from PwC Identity">
                        <TextField slotProps={slotProps} className="details-apps-text" margin="dense" id="editItem_urn" label="URN" defaultValue={model?.urn} disabled={true} />
                    </HtmlTooltip>

                    <HtmlTooltip title="The application belongs to this territory">
                        <TextField slotProps={slotProps} className="details-apps-text" margin="dense" id="editItem_territoryName" label="Territory" defaultValue={model?.territoryName} disabled={true} />
                    </HtmlTooltip>
                    </div>
                <div>
                    <HtmlTooltip title="This person is the delivery manager from the MFRM team who is responsible for the territory">
                        <TextField slotProps={slotProps} className="details-apps-text" margin="dense" id="editItem_deliverManagerName" label="Delivery Manager" defaultValue={model?.deliverManagerName} disabled={true} />
                    </HtmlTooltip>

                    <HtmlTooltip
                        title="The engineer from the integration team primarily responsible for supporting the migration of this application"
                        hideTooltipOnChildrenExpand={true}
                    >
                        <UserTextBox canBeEmpty={true} handleValidChange={setIsIntEngValid} handleChange={(e) => { setModelIdasTeamEmail(e?.email); setModelIdasTeamName(e?.name); }} label="Integration Engineer" defaultValue={{ email: modelIdasTeamEmail, name: modelIdasTeamName }} disabled={isViewMode || userInfo?.isAdminOrMt !== true} />
                    </HtmlTooltip>
                    </div>
                <div>
                    <HtmlTooltip title="The protocol that this application uses for SSO authentication">
                        <TextField slotProps={slotProps} className="details-apps-text" margin="dense" id="editItem_authenticationMethod" label="Protocol" defaultValue={model?.authenticationMethod} disabled={true} />
                    </HtmlTooltip>

                    <HtmlTooltip title="Application SNow Configuration Item (CI) unique identifier number">
                        <TextField slotProps={slotProps} className="details-apps-text" margin="dense" id="editItem_snowFormNumber" label="CI Number" defaultValue={model?.ci} disabled={true} />
                    </HtmlTooltip>
                    </div>
                    <div>
                    {userInfo?.isAdminOrMt === true ?
                        isViewMode ? <TextField slotProps={slotProps} className="details-apps-text" margin="dense" id="editItem_listedContact" label="Listed contact" defaultValue={getListedContactsAsString(modelListedContact)} disabled={true} />
                            : <ListedContactTextBox label="Listed contact" defaultValue={modelListedContact} handleChange={setModelListedContact} />
                        : ''
                    }
                        <TextField slotProps={slotProps} className="details-apps-text" margin="dense" onChange={(e) => model.intAuthLevel = e.target.value} id="editItem_intAuthLevel" label="Internal Authentication" defaultValue={model?.intAuthLevel} disabled={isViewMode || (userInfo?.isAdminOrMt !== true && model?.disabledForOwner !== false)} />
                </div>
                <div>
                    <HtmlTooltip title="The ARR number confirmed to be associated with this URN.  If the URN is a production application and there is no ARR, this app will be blocked from migration">
                        <TextField slotProps={slotProps} className="details-apps-text" margin="dense" id="editItem_snowArrNumber" label="ARR" defaultValue={model?.snowArrNumber} disabled={true} />
                    </HtmlTooltip>

                    <HtmlTooltip title="Environment indicates which environment from PwC Identity this URN was discovered from.  This DOES NOT necessarily reflect whether the URN itself is Dev/Stage or Prod">
                        <TextField slotProps={slotProps} className="details-apps-text" margin="dense" label="Environment" defaultValue={model?.targetEnvironment} disabled={true} />
                    </HtmlTooltip>
                </div>

                <br />
                <Typography className="details-apps-group">Status History</Typography>
                <Typography className="details-apps-title">Timeline of migration status changes for this application</Typography>

                <div className="status-timeline-wrapper" ref={timelineWrapperRef}>
                    <StatusTimeline statusHistory={modelStatusHistory} scrollContainerRef={timelineWrapperRef} />
                </div>

                <AppDetailsEntraInfo model={model} isViewMode={isViewMode} setModelOwnerConsentDate={setModelOwnerConsentDate} modelOwnerConsentDate={modelOwnerConsentDate} handleElementUpdate={handleElementUpdate} />
                    <br />
                    <Typography className="details-apps-group">Definition of Ready</Typography>
                <Typography className="details-apps-title">The statuses of all prerequisites before your application can be migrated to Entra ID <br />
                    If your application has GUM or LDAP dependencies, please follow this guidance <a href="https://migration.idaas.pwc.com/docs/complex-applications/">link</a>
                </Typography>

                <div>
                    <HtmlTooltip
                        title="This URN relies on GUM in some way and should be migrated in collaboration with the integration team.  App owners can edit this attribute within the portal"
                    >
                        {isViewMode
                            ? <TextField slotProps={slotProps} className="details-apps-text-with-check" margin="dense" label="GUM" defaultValue={modelIsGum == null ? "Empty" : modelIsGum === true ? "True" : "False"} disabled={true} />
                            : <Autocomplete disabled={userInfo?.isAdminOrMt !== true && model?.disabledForOwner !== false} className="details-apps-autocomplete-with-check" value={modelIsGum == null ? "Empty" : modelIsGum === true ? "True" : "False"} onChange={(e, v) => setModelIsGum(v === "Empty" ? null : v === "True" ? true : false)} options={optionsTrueFalseEmpty} renderInput={(params) => <TextField slotProps={slotProps} {...params} className="details-apps-autocomplete-inner" margin="dense" label="GUM" />} />
                        }
                    </HtmlTooltip>
                    { modelIsGum === true
                        ? <HtmlTooltip title="This application uses GUM and is not currently eligible for migration. It will be eligible soon as support is added."><ErrorIcon htmlColor="red" className="details-check-icon" /></HtmlTooltip>
                        : <HtmlTooltip title="Migration requirement is met"><TaskAltIcon htmlColor="green" className="details-check-icon" /></HtmlTooltip>
                    }

                    <HtmlTooltip
                        title="This URN relies on LDAP in some way and should be migrated in collaboration with the integration team.  App owners can edit this attribute within the portal"
                    >
                    {isViewMode
                        ? <TextField slotProps={slotProps} className="details-apps-text-with-check" margin="dense" label="LDAP" defaultValue={modelIsLdap == null ? "Empty" : modelIsLdap === true ? "True" : "False"} disabled={true} />
                        : <Autocomplete disabled={userInfo?.isAdminOrMt !== true && model?.disabledForOwner !== false} className="details-apps-autocomplete-with-check" value={modelIsLdap == null ? "Empty" : modelIsLdap === true ? "True" : "False"} onChange={(e, v) => setModelIsLdap(v === "Empty" ? null : v === "True" ? true : false)} options={optionsTrueFalseEmpty} renderInput={(params) => <TextField slotProps={slotProps} {...params} className="details-apps-autocomplete-inner" margin="dense" label="LDAP" />} />
                    }
                    </HtmlTooltip>
                    { modelIsLdap === true
                        ? <HtmlTooltip title="This application relies on LDAP, which is not supported for migration yet. It will be eligible soon as support is added."><ErrorIcon htmlColor="red" className="details-check-icon" /></HtmlTooltip>
                        : <HtmlTooltip title="Migration requirement is met"><TaskAltIcon htmlColor="green" className="details-check-icon" /></HtmlTooltip>
                    }
                </div>
                <div>
                    <HtmlTooltip title="The ARR status for the ARR associated with this URN.  If the URN is a production application and there is no ARR, this app will be blocked from migration">
                        <TextField slotProps={slotProps} className="details-apps-text-with-check" margin="dense" label="ARR status" defaultValue={model?.arrStatus} disabled={true} />
                    </HtmlTooltip>
                    {model?.isArrStatusValid !== true
                        ? <HtmlTooltip title="AAR Status is 'not completed'. It will be eligible after the ARR for this application is complete."><ErrorIcon htmlColor="red" className="details-check-icon" /></HtmlTooltip>
                        : <HtmlTooltip title="Migration requirement is met"><TaskAltIcon htmlColor="green" className="details-check-icon" /></HtmlTooltip>
                    }

                    <HtmlTooltip title="This indicates whether an application leverages client federation.  If it does, the URN will be blocked from migration">
                        <TextField slotProps={slotProps} className="details-apps-text-with-check" margin="dense" label="Has Client Federation" defaultValue={model?.entraIdProjectStatus} disabled={true} />
                    </HtmlTooltip>
                    {isBlocked(model?.entraIdProjectStatus)
                        ? <HtmlTooltip title="Migration is blocked due to project status"><ErrorIcon htmlColor="red" className="details-check-icon" /></HtmlTooltip>
                        : <HtmlTooltip title="Migration requirement is met"><TaskAltIcon htmlColor="green" className="details-check-icon" /></HtmlTooltip>
                    }
                </div>
                <div>
                    <HtmlTooltip title="Indicates whether PwC identity considers this active, or decommissioned">
                        <TextField slotProps={slotProps} className="details-apps-text-with-check" margin="dense" label="Active on PwC Identity" defaultValue={model?.isForgerockStatusValid === true ? "Active" : "Inactive"} disabled={true} />
                    </HtmlTooltip>
                    {model?.isForgerockStatusValid !== true
                        ? <HtmlTooltip title="Application is  not active in Forgerock and should therefore not be migrated to Entra ID."><ErrorIcon htmlColor="red" className="details-check-icon" /></HtmlTooltip>
                        : <HtmlTooltip title="Migration requirement is met"><TaskAltIcon htmlColor="green" className="details-check-icon" /></HtmlTooltip>
                    }

                    <HtmlTooltip title='If any one of the 6 "people" attributes has an admin account in Entra, the application is allowed to be migrated'>
                        <TextField slotProps={slotProps} className="details-apps-text-with-check" margin="dense" label="Application Owner has an admin account on EntraID" defaultValue={model?.hasEntraAdminAccount === true ? "Yes" : "No"} disabled={true} />
                    </HtmlTooltip>
                    {model?.hasEntraAdminAccount !== true
                        ? <HtmlTooltip title="The Entra admin account does not exist for the app owner yet. It will be eligible once the account has been created."><ErrorIcon htmlColor="red" className="details-check-icon" /></HtmlTooltip>
                        : <HtmlTooltip title="Migration requirement is met"><TaskAltIcon htmlColor="green" className="details-check-icon" /></HtmlTooltip>
                    }
                </div>
                <AppDetailsTemporaryPrerequisites isViewMode={isViewMode} model={model} userInfo={userInfo} handleElementUpdate={handleElementUpdate} />
                    {userInfo?.isAdminOrMt === true ?
                        <div>
                        <Typography className="details-apps-group">Comments</Typography>
                        <HtmlTooltip title="Free text field for comments relating to this URN">
                            <TextField slotProps={slotProps} fullWidth={true} multiline={true} maxRows={6} minRows={2} style={{ margin: '5px', marginTop: '30px' }} margin="dense" onChange={(e) => model.comments = e.target.value} id="editItem_comments" label="Comments" defaultValue={model?.comments} disabled={isViewMode} />
                        </HtmlTooltip>
                        </div>
                        : ''
                }
                    {userInfo?.isAdminOrMt === true && isFailedReasonVisible === true ?
                    <div>
                        <Typography className="details-apps-group">Failed reason</Typography>
                        <TextField slotProps={slotProps} fullWidth={true} multiline={true} maxRows={6} minRows={2} style={{ margin: '5px', marginTop: '30px' }} margin="dense" id="editItem_failedReason" label="Failed Reason" onChange={(e) => setModelFailedReason(e.target.value)} defaultValue={modelFailedReason} disabled={isFailedReasonDisabled} required={isFailedReasonDisabled === false} />
                        <TextField slotProps={slotProps} className="details-apps-text" margin="dense" id="editItem_failedDate" label="Failed Date" defaultValue={(model?.failedDate == null) ? null : dayjs(model?.failedDate).format('YYYY-MM-DD HH:mm:ss')} disabled={true} />

                    </div>
                        : ''
                    }
                    { decommisionButtonVisible() ?
                        <Button style={{ margin: '10px', float: 'left', width: '220px', backgroundColor: '#C52A1A', textTransform: 'none' }} variant="contained" color="success" onClick={() => handleDecommision()}>Decommission this app</Button>
                    : ''}
                    { userInfo?.isAdminOrMt !== true && userInfo?.isAppOwner !== true ? '' :
                      isViewMode
                        ? <Button style={{ margin: '10px', float: 'right', width: '100px', backgroundColor: '#FD5107', textTransform: 'none' }} variant="contained" color="success" onClick={() => setIsViewMode(false)}>Edit</Button>
                        : <Button style={{ margin: '10px', float: 'right', width: '100px', backgroundColor: '#FD5107', textTransform: 'none' }} variant="contained" color="success" onClick={onSaveClick} disabled={isSaveButtonEnabled !== true}>Save</Button>
                    }
                <Button style={{ margin: '10px', float: 'right', width: '100px', backgroundColor: 'white', color: '#FD5107', borderColor: '#FD5107', borderStyle: 'solid', borderWidth: '10', textTransform: 'none' }} variant="outlined" color="warning" onClick={handleClose}>Cancel</Button>
                <AppOwnerSelect handleClose={handleOwnerSelectClose} handleSave={handleSave} isVisible={isAppOwnerSelectModalVisible} defaultValue={modelListedContact == null || modelListedContact?.length == 0 ? null : modelListedContact[0]} setAppOwner={setModelUserEmail} />
            </DialogContent>
        </Dialog>
    );
};

export default AppDetails;